"use client";
import { useQuery, useInfiniteQuery } from "@tanstack/react-query";
import { apiClient } from "@/lib/tanstack/apiClient";
import { userConfig } from "@/queries/pages/dashboard/userApiConfig";
import { roleConfig } from "@/queries/pages/dashboard/roleApiConfig";
import { organizationConfig } from "@/queries/pages/dashboard/organizationApiConfig";
import { entityConfig } from "@/queries/pages/dashboard/masterDataApiConfig";
import { accessMatrixConfig } from "@/queries/pages/dashboard/accessMatrixApiConfig";
import { bookingsConfig } from "@/queries/pages/dashboard/bookingsApiConfig";
import { staffConfig } from "@/queries/pages/dashboard/staffApiConfig";
import { housekeepingConfig } from "@/queries/pages/dashboard/housekeepingApiConfig";
import { registrationConfig } from "@/queries/pages/dashboard/registrationApiConfig";
import { propertyInfoConfig } from "@/queries/pages/dashboard/propertyInfoApiConfig";
import { propertyComplianceConfig } from "@/queries/pages/dashboard/propertyComplianceApiConfig";

import { guestConfig } from "@/queries/pages/dashboard/guestsApiConfig";
import { calendarConfig } from "@/queries/pages/dashboard/calendarApiConfig";
import { logConfig } from "@/queries/pages/dashboard/logApiConfig";
import { profileConfig } from "@/queries/pages/dashboard/profileApiConfig";
import { dynamicPricingConfig } from "@/queries/pages/pricing/pricing-rule.apiconfig";
import { roomTypeRateBoundariesApiConfig } from "@/queries/pages/pricing/pricing-caps.apiconfig";
import { propertyConfig } from "@/queries/pages/dashboard/propertyApiConfig";
import { roomConfig } from "@/queries/pages/dashboard/roomApiConfig";
import { roomTypeConfig } from "@/queries/pages/dashboard/roomTypeApiConfig";
import { buildingConfig } from "@/queries/pages/dashboard/buildingApiConfig";
import { bedTypeConfig } from "@/queries/pages/dashboard/bedTypeApiConfig";
import { boardPlanConfig } from "@/queries/pages/dashboard/boardPlanApiConfig";
import { ratePlanApiConfig } from "@/queries/pages/pricing/rate-plan.apiconfig";
import { cancellationPolicyApiConfig } from "@/queries/pages/pricing/cancellation-policy.apiconfig";
import { earlyDeparturePolicyApiConfig } from "@/queries/pages/pricing/early-departure-policy.apiconfig";
import { subscriptionPlanApiConfig } from "@/queries/pages/pricing/subscription-plan.apiconfig";
import { subscriptionConfig } from "@/queries/pages/dashboard/subscriptionApiConfig";
import { holdsConfig } from "@/queries/pages/dashboard/holdsApiConfig";
import { onboardingConfig } from "@/queries/pages/dashboard/onboardingApiConfig";
import { authConfig } from "@/queries/pages/dashboard/authApiConfig";
import { guestReviewConfig } from "@/queries/pages/dashboard/guestReviewApiConfig";
import { summaryConfig } from "@/queries/pages/dashboard/ReviewSummaryApiConfig";
import { notificationTemplatesConfig } from "@/queries/pages/dashboard/notificationApiConfig";
import { maintenanceConfig } from "@/queries/pages/dashboard/maintenanceApiConfig";
import { grievanceConfig } from "@/queries/pages/dashboard/grievanceApiConfig";
import { zatcaConfig } from "@/queries/pages/dashboard/zatcaApiConfig";
import { propertyAddonConfig } from "@/queries/pages/dashboard/propertyAddonApiConfig";
import { folioConfig } from "@/queries/pages/dashboard/folioApiConfig";
import { bookingAddonConfig } from "@/queries/pages/dashboard/bookingAddonApiConfig";
import { propertyPolicyConfig } from "@/queries/pages/dashboard/propertyPolicyApiConfig";
import { paymentTypesConfig } from "@/queries/pages/dashboard/paymentTypesApiConfig";
import { faqConfig } from "@/queries/pages/dashboard/faqApiConfig";
import { nightAuditConfig } from "@/queries/pages/dashboard/nightAuditApiConfig";
import { consumableItemConfig } from "@/queries/pages/dashboard/consumableItemApiConfig";
import { parLevelConfig } from "@/queries/pages/dashboard/parLevelApiConfig";
import { roomInventoryConfig } from "@/queries/pages/dashboard/roomInventoryApiConfig";
import { inspectionConfig } from "@/queries/pages/dashboard/inspectionApiConfig";

const ALL_API_CONFIGS = {
  ...userConfig,
  ...roleConfig,
  ...organizationConfig,
  ...entityConfig,
  ...accessMatrixConfig,
  ...bookingsConfig,
  ...calendarConfig,
  ...staffConfig,
  ...housekeepingConfig,
  ...registrationConfig,
  ...propertyInfoConfig,
  ...propertyComplianceConfig,

  ...guestConfig,
  ...profileConfig,
  ...subscriptionConfig,
  ...logConfig,
  ...dynamicPricingConfig,
  ...roomTypeRateBoundariesApiConfig,
  ...propertyConfig,
  ...roomConfig,
  ...roomTypeConfig,
  ...buildingConfig,
  ...bedTypeConfig,
  ...boardPlanConfig,
  ...ratePlanApiConfig,
  ...cancellationPolicyApiConfig,
  ...subscriptionPlanApiConfig,
  ...earlyDeparturePolicyApiConfig,
  ...subscriptionConfig,
  ...holdsConfig,
  ...onboardingConfig,
  ...authConfig,
  ...guestReviewConfig,
  ...summaryConfig,
  ...notificationTemplatesConfig,
  ...maintenanceConfig,
  ...grievanceConfig,
  ...zatcaConfig,
  ...propertyAddonConfig,
  ...folioConfig,
  ...bookingAddonConfig,
  ...propertyPolicyConfig,
  ...paymentTypesConfig,
  ...faqConfig,
  ...nightAuditConfig,
  ...consumableItemConfig,
  ...parLevelConfig,
  ...roomInventoryConfig,
  ...inspectionConfig,
};

export function useFetchQuery(configOrKey, params = {}, options = {}) {
  // Support both direct config object and config key string
  const config =
    typeof configOrKey === "string"
      ? ALL_API_CONFIGS[configOrKey]
      : configOrKey;

  // Validate config exists
  if (!config) {
    const configKey = typeof configOrKey === "string" ? configOrKey : "unknown";
    console.log(
      `API configuration not found for key: "${configKey}". Please check your API configuration.`,
    );
  }

  // Validate config.url exists
  if (!config.url) {
    const configKey = typeof configOrKey === "string" ? configOrKey : "unknown";
    console.log(
      `API configuration is missing 'url' property for key: "${configKey}".`,
    );
  }

  let url = typeof config.url === "function" ? config.url(params) : config.url;
  const method = (config.method || "GET").toUpperCase();

  // For POST/PUT/PATCH, send params in body only (avoid query-string mismatch)
  if (!["POST", "PUT", "PATCH"].includes(method)) {
    // Build URL with query parameters for idempotent requests
    if (
      params &&
      typeof params === "object" &&
      typeof config.url !== "function" &&
      !url.includes("?")
    ) {
      const queryParams = new URLSearchParams();
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== "") {
          queryParams.append(key, value);
        }
      });
      const queryString = queryParams.toString();
      if (queryString) {
        url += `${url.includes("?") ? "&" : "?"}${queryString}`;
      }
    } else if (
      params &&
      typeof params === "object" &&
      typeof config.url !== "function" &&
      Object.keys(params).length > 0
    ) {
      const hasQueryParams = url.includes("?");
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== "") {
          url += `${
            hasQueryParams || url.includes("&") ? "&" : "?"
          }${key}=${encodeURIComponent(value)}`;
        }
      });
    }
  }

  return useQuery({
    queryKey: [config.key, params],
    queryFn: async () => {
      const requestOptions = { method };
      if (
        ["POST", "PUT", "PATCH"].includes(method) &&
        params &&
        Object.keys(params).length > 0
      ) {
        requestOptions.body = JSON.stringify(params);
      }
      const response = await apiClient(url, requestOptions);
      if (!response.ok) {
        console.log(response.message || "Request failed");
        return null;
      }
      return response.data;
    },
    ...config.options,
    ...options,
  });
}

export function useInfiniteScrollQuery(configOrKey, params = {}, options = {}) {
  const { limit = 10, ...queryOptions } = options;

  const config =
    typeof configOrKey === "string"
      ? ALL_API_CONFIGS[configOrKey]
      : configOrKey;

  if (!config) {
    const key = typeof configOrKey === "string" ? configOrKey : "unknown";
    console.log(`API configuration not found for key: "${key}"`);
  }

  return useInfiniteQuery({
    queryKey: [config?.key, "infinite", params],
    queryFn: async ({ pageParam = 1 }) => {
      const baseUrl = typeof config.url === "function" ? config.url(params) : config.url;
      const qp = new URLSearchParams({ page: String(pageParam), limit: String(limit) });
      Object.entries(params).forEach(([k, v]) => {
        if (v !== undefined && v !== null && v !== "") qp.set(k, String(v));
      });
      const res = await apiClient(`${baseUrl}?${qp}`);
      if (!res.ok) throw new Error(res.message || "Request failed");
      return res.data;
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage) => {
      const p = lastPage?.data?.pagination;
      return p && p.page < p.totalPages ? p.page + 1 : undefined;
    },
    ...config?.options,
    ...queryOptions,
  });
}
